import { useState } from 'react';

type UseControllableStateParams<Value> = {
  controlled: Value | undefined;
  defaultValue: Value;
  onChange?: (value: Value) => void;
};

// Standard controlled/uncontrolled state helper (same idea as MUI's
// useControlled). When `controlled` is defined the component is controlled and
// only reports changes via `onChange`; otherwise it keeps its own state seeded
// from `defaultValue`. Returns a useState-like tuple.
export const useControllableState = <Value>(
  params: UseControllableStateParams<Value>,
) => {
  const { controlled, defaultValue, onChange } = params;

  const [uncontrolled, setUncontrolled] = useState(defaultValue);
  const isControlled = controlled !== undefined;
  const value = isControlled ? controlled : uncontrolled;

  const setValue = (next: Value) => {
    if (!isControlled) setUncontrolled(next);
    onChange?.(next);
  };

  return [value, setValue] as const;
};
